import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { Alert, Confirm, Modal, EmptyState, ErrorState, SearchableSelect, TableSkeleton } from '../components';
import ManualTriggerPanel from '../components/ManualTriggerPanel';
import ExecutionTracePanel from '../components/execution-trace/ExecutionTracePanel';
import { PageHeader, Toolbar } from '../components/layout';

const LogsPage = ({ canCancelQueryTriggers = false }) => {
  const [showManualTrigger, setShowManualTrigger] = useState(false);
  const [manualTriggerBusy, setManualTriggerBusy] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const initialFilters = {
    issue_key: '',
    group_name: '',
    status: '',
    page: 1,
    page_size: 20
  };
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [selectedLog, setSelectedLog] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState('overview');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [confirmState, setConfirmState] = useState({ isOpen: false, message: '', onConfirm: null });
  const [alertState, setAlertState] = useState({ isOpen: false, message: '' });
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        const response = await api.getLogs({
          ...appliedFilters,
          page: currentPage,
          page_size: appliedFilters.page_size
        });
        const data = response.data;

        if (data && Array.isArray(data.logs)) {
          setLogs(data.logs);
          setTotalLogs(data.total || 0);
          setTotalPages(data.total_pages || 1);
        } else {
          setLogs([]);
          console.warn('API返回的日志数据格式不正确:', data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [appliedFilters, currentPage, refreshKey]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setDraftFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = () => {
    setAppliedFilters({
      ...draftFilters,
      page: 1
    });
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      success: { class: 'badge-success', label: '成功' },
      pending: { class: 'badge-warning', label: '待处理' },
      running: { class: 'badge-info', label: '处理中' },
      cancelled: { class: 'badge-default', label: '已终止' },
      failed: { class: 'badge-danger', label: '失败' }
    };
    const config = statusMap[status] || { class: 'badge-default', label: status };
    return <span className={`badge ${config.class}`}>{config.label}</span>;
  };

  const handleViewDetail = async (log) => {
    setActiveDetailTab('overview');
    setDetailLoading(true);
    setDetailError(null);
    try {
      const response = await api.getLogDetail(log.id);
      setSelectedLog(response.data);
    } catch (err) {
      setDetailError(err.message);
      setSelectedLog(log);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setSelectedLog(null);
    setDetailError(null);
    setActiveDetailTab('overview');
  };

  const handleRetry = async (log, e) => {
    e.stopPropagation();
    if (!log.issue_key) {
      setAlertState({ isOpen: true, message: '该记录没有工单 Key，无法重试' });
      return;
    }
    setConfirmState({
      isOpen: true,
      message: `确定要重新处理工单 ${log.issue_key} 吗？`,
      onConfirm: async () => {
        try {
          await api.retryLog(log.id);
          setAlertState({ isOpen: true, message: '已触发重试，请稍后刷新查看结果' });
        } catch (err) {
          setAlertState({ isOpen: true, message: '重试失败: ' + (err.response?.data?.detail || err.message) });
        }
        setConfirmState({ ...confirmState, isOpen: false });
      }
    });
  };

  const handleCancelPendingQueryTriggers = () => {
    setCancelConfirmOpen(true);
  };

  const confirmCancelPendingQueryTriggers = async () => {
    if (cancelLoading) return;
    setCancelLoading(true);
    try {
      const response = await api.cancelPendingQueryTriggers();
      setCancelConfirmOpen(false);
      setAlertState({
        isOpen: true,
        message: `已终止 ${response.data.cancelled || 0} 个查询待处理任务`
      });
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      setAlertState({
        isOpen: true,
        message: '终止失败: ' + (err.response?.data?.detail || err.message)
      });
    } finally {
      setCancelLoading(false);
    }
  };

  const getJiraIssueUrl = (issueKey) => {
    if (!issueKey) return null;
    return `https://shopline.atlassian.net/browse/${issueKey}`;
  };

  const statusCounts = logs.reduce((acc, log) => {
    acc[log.status] = (acc[log.status] || 0) + 1;
    return acc;
  }, {});

  if (error) {
    return (
      <ErrorState
        title="无法加载操作记录"
        description={error}
        onRetry={() => setRefreshKey(prev => prev + 1)}
      />
    );
  }

  return (
    <div className="operations-list-page operation-log-page">
      <PageHeader
        title="操作记录"
        actions={<>
          <button type="button" className="btn btn-primary" onClick={() => setShowManualTrigger(true)}>手动触发自动化</button>
          {canCancelQueryTriggers ? (
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleCancelPendingQueryTriggers}
            disabled={cancelLoading}
          >
            {cancelLoading ? '终止中...' : '终止查询待处理任务'}
          </button>
        ) : null}</>}
      />

      {showManualTrigger && <Modal isOpen title="手动触发自动化" size="large" className="manual-trigger-dialog" busy={manualTriggerBusy}
        onClose={() => { setShowManualTrigger(false); setRefreshKey(key => key + 1); }}>
        <ManualTriggerPanel embedded onBusyChange={setManualTriggerBusy} />
      </Modal>}

      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">当前筛选总数</div>
          <div className="metric-value">{totalLogs}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">本页成功</div>
          <div className="metric-value">{statusCounts.success || 0}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">本页待处理</div>
          <div className="metric-value">{statusCounts.pending || 0}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">本页失败</div>
          <div className="metric-value">{statusCounts.failed || 0}</div>
        </div>
      </div>

      <Toolbar className="page-toolbar">
        <div className="form-group" style={{ flex: 1, minWidth: '200px', marginBottom: 0 }}>
          <label className="form-label">工单 Key</label>
          <input
            type="text"
            name="issue_key"
            value={draftFilters.issue_key}
            onChange={handleFilterChange}
            className="form-input"
            placeholder="搜索工单 Key"
          />
        </div>
        <div className="form-group" style={{ flex: 1, minWidth: '200px', marginBottom: 0 }}>
          <label className="form-label">规则组</label>
          <input
            type="text"
            name="group_name"
            value={draftFilters.group_name}
            onChange={handleFilterChange}
            className="form-input"
            placeholder="搜索规则组"
          />
        </div>
        <div className="form-group" style={{ width: '160px', marginBottom: 0 }}>
          <label className="form-label">状态</label>
          <SearchableSelect
            value={draftFilters.status}
            options={[
              { value: '', label: '全部状态' },
              { value: 'success', label: '成功' },
              { value: 'pending', label: '待处理' },
              { value: 'running', label: '处理中' },
              { value: 'cancelled', label: '已终止' },
              { value: 'failed', label: '失败' }
            ]}
            onChange={(val) => handleFilterChange({ target: { name: 'status', value: val } })}
            placeholder="选择状态"
            searchable={false}
          />
        </div>
        <div className="form-group" style={{ alignSelf: 'flex-end', marginBottom: 0 }}>
          <button className="btn btn-primary" onClick={handleSearch}>搜索</button>
        </div>
      </Toolbar>

      <section className="operation-log-list-surface">
        <header className="operation-list-surface-header">
          <h2>日志明细</h2>
          <span className="operation-list-count">{totalLogs} 条</span>
        </header>
        <div className="operation-list-surface-body">
        {loading ? (
          <div className="logs-table-skeleton">
            <TableSkeleton rowCount={8} columnCount={7} label="加载操作记录中" />
          </div>
        ) : logs.length === 0 ? (
          <EmptyState title="暂无操作记录" description="新的处理结果会显示在这里。" />
        ) : (
          <>
          <div className="table-container operation-log-table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table className="table operation-log-table">
              <colgroup>
                <col className="operation-log-col-issue" />
                <col className="operation-log-col-group" />
                <col className="operation-log-col-event" />
                <col className="operation-log-col-action" />
                <col className="operation-log-col-status" />
                <col className="operation-log-col-time" />
                <col className="operation-log-col-controls" />
              </colgroup>
              <thead>
                <tr>
                  <th>工单 Key</th>
                  <th>规则组</th>
                  <th>事件类型</th>
                  <th>操作</th>
                  <th style={{ width: '100px' }}>状态</th>
                  <th style={{ width: '150px' }}>时间</th>
                  <th style={{ width: '140px' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const jiraUrl = getJiraIssueUrl(log.issue_key);
                  return (
                    <tr key={log.id} className={`operation-log-row status-${log.status || 'default'}`}>
                      <td className="operation-log-issue">
                        {log.issue_key ? (
                          jiraUrl ? (
                            <a
                              href={jiraUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="badge badge-default"
                              style={{ textDecoration: 'none', cursor: 'pointer' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {log.issue_key}
                            </a>
                          ) : (
                            <span className="badge badge-default">{log.issue_key}</span>
                          )
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>-</span>
                        )}
                      </td>
                      <td className="operation-log-group">{log.group_name || <span className="operation-log-empty">-</span>}</td>
                      <td className="operation-log-event">{log.event_type || '-'}</td>
                      <td className="operation-log-action">{log.action || '-'}</td>
                      <td className="operation-log-status">{getStatusBadge(log.status)}</td>
                      <td className="operation-log-time"><time>{log.created_at || '-'}</time></td>
                      <td className="operation-log-actions">
                        <div className="operation-log-action-buttons">
                          <button className="btn btn-secondary btn-sm" onClick={() => handleViewDetail(log)}>查看</button>
                          {log.issue_key && (
                            <button className="btn btn-primary btn-sm" onClick={(e) => handleRetry(log, e)} title="重新触发自动化流程">
                              重试
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="operation-log-mobile-list responsive-operation-list">
            {logs.map(log => {
              const jiraUrl = getJiraIssueUrl(log.issue_key);
              return (
                <article className="responsive-operation-row" key={log.id}>
                  <div className="responsive-operation-main">
                    <strong>{log.issue_key || `#${log.id}`}</strong>
                    <span>{log.action || '-'}</span>
                    <small>{log.group_name || log.event_type || '-'}</small>
                  </div>
                  {getStatusBadge(log.status)}
                  <div className="responsive-operation-meta">
                    <time>{log.created_at || '-'}</time>
                    <div className="operation-log-mobile-actions">
                      {jiraUrl && (
                        <a href={jiraUrl} target="_blank" rel="noopener noreferrer">Jira</a>
                      )}
                      <button className="btn btn-secondary btn-sm" onClick={() => handleViewDetail(log)}>查看</button>
                      {log.issue_key && (
                        <button className="btn btn-primary btn-sm" onClick={(e) => handleRetry(log, e)}>重试</button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          </>
        )}

        {!loading && logs.length > 0 && (
          <div className="operation-list-surface-footer">
            <div className="pagination" style={{ margin: 0 }}>
              <div className="pagination-info">共 {totalLogs} 条记录，第 {currentPage} / {totalPages} 页</div>
              <div className="pagination-controls">
                <button className="btn btn-secondary btn-sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>上一页</button>
                <span style={{ padding: '0 12px', fontSize: '14px', color: 'var(--text-secondary)' }}>{currentPage}</span>
                <button className="btn btn-secondary btn-sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>下一页</button>
              </div>
            </div>
          </div>
        )}
        </div>
      </section>

      {selectedLog && (
        <Modal isOpen title={<>日志详情</>} onClose={handleCloseDetail} busy={confirmState.isOpen} size="large" className="admin-standard-dialog operation-log-detail-modal">
<p className="operation-log-detail-subtitle">
                  #{selectedLog.id} · {selectedLog.issue_key || '无工单 Key'} · {selectedLog.group_name || '无规则组'}
                </p>

            <div className="operation-log-tabs" role="tablist" aria-label="日志详情">
              {[
                ['overview', '概览'],
                ['trace', '判断流程'],
                ['payload', '原始报文']
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={activeDetailTab === id}
                  className={activeDetailTab === id ? 'active' : ''}
                  onClick={() => setActiveDetailTab(id)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="modal-body operation-log-detail-body">
              {detailLoading ? (
                <TableSkeleton rowCount={5} columnCount={2} label="加载日志详情中" />
              ) : (
                <>
                  {detailError && (
                    <div className="operation-log-detail-error">
                      加载详情失败: {detailError}，显示列表缓存数据
                    </div>
                  )}
                  {activeDetailTab === 'overview' && (
                    <div className="operation-log-overview">
                      <div className="operation-log-metadata">
                        {[
                          ['ID', `#${selectedLog.id}`],
                          ['事件类型', selectedLog.event_type || '-'],
                          ['规则组', selectedLog.group_name || '-'],
                          ['操作', selectedLog.action || '-'],
                          ['状态', getStatusBadge(selectedLog.status)],
                          ['时间', selectedLog.created_at || '-']
                        ].map(([label, value]) => (
                          <div className="operation-log-field" key={label}>
                            <span>{label}</span>
                            <div>{value}</div>
                          </div>
                        ))}
                        <div className="operation-log-field">
                          <span>工单 Key</span>
                          <div>
                            {selectedLog.issue_key ? (
                              <a
                                href={getJiraIssueUrl(selectedLog.issue_key)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {selectedLog.issue_key}
                              </a>
                            ) : '-'}
                          </div>
                        </div>
                      </div>
                      {selectedLog.details && (
                        <section className="operation-log-text-section">
                          <h4>执行详情</h4>
                          <pre>{selectedLog.details}</pre>
                        </section>
                      )}
                      {selectedLog.error_message && (
                        <section className="operation-log-text-section status-error">
                          <h4>错误信息</h4>
                          <pre>{selectedLog.error_message}</pre>
                        </section>
                      )}
                    </div>
                  )}
                  {activeDetailTab === 'trace' && (
                    <ExecutionTracePanel
                      trace={selectedLog.execution_trace}
                      traceError={selectedLog.execution_trace_error}
                    />
                  )}
                  {activeDetailTab === 'payload' && (
                    selectedLog.payload ? (
                      <pre className="operation-log-payload">
                        {JSON.stringify(selectedLog.payload, null, 2)}
                      </pre>
                    ) : (
                      <div className="operation-log-empty-tab">
                        当前日志没有原始报文
                      </div>
                    )
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              {selectedLog.issue_key && <button className="btn btn-primary" onClick={() => handleRetry(selectedLog, { stopPropagation: () => {} })}>重试</button>}
              <button className="btn btn-secondary" onClick={handleCloseDetail}>关闭</button>
            </div>

        </Modal>
      )}

      <Confirm
        isOpen={confirmState.isOpen}
        title="确认操作"
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={() => setConfirmState({ ...confirmState, isOpen: false })}
      />

      <Confirm
        isOpen={cancelConfirmOpen}
        title="终止查询待处理任务"
        message="将终止所有尚未开始的立即查询和定时查询待处理任务。正在处理的任务会继续执行，定时任务配置仍保持启用。"
        onConfirm={confirmCancelPendingQueryTriggers}
        onCancel={() => setCancelConfirmOpen(false)}
        confirmText="确认终止"
        loading={cancelLoading}
        disabled={cancelLoading}
        loadingText="终止中..."
      />

      <Alert
        isOpen={alertState.isOpen}
        title="提示"
        message={alertState.message}
        onClose={() => setAlertState({ ...alertState, isOpen: false })}
      />
    </div>
  );
};

export default LogsPage;
